import { useQuery } from "@tanstack/react-query";
import { supabaseAgency } from "@/lib/supabase";

export interface ClientSnapshot {
  client: string;
  last_meeting_at: string | null;
  last_meeting_title: string | null;
  open_projects: number;
  updates_7d: number;
  open_tasks: number;
  synced_at: string | null;
}

export function useClientSnapshot(client: string) {
  return useQuery<ClientSnapshot>({
    queryKey: ["client_snapshot", client],
    queryFn: async () => {
      const sevenDaysAgo = new Date(Date.now() - 7 * 86400_000).toISOString();

      const [meetings, projects, recent, tasks] = await Promise.all([
        supabaseAgency.from("os_vault_articles_cache")
          .select("title, updated_at")
          .eq("client", client).eq("kind", "meeting")
          .order("updated_at", { ascending: false }).limit(1),
        supabaseAgency.from("os_vault_articles_cache")
          .select("slug", { count: "exact" })
          .eq("client", client).eq("kind", "project"),
        supabaseAgency.from("os_vault_articles_cache")
          .select("slug, updated_at", { count: "exact" })
          .eq("client", client).gte("updated_at", sevenDaysAgo),
        supabaseAgency.from("linear_issues_cache")
          .select("issue_id", { count: "exact" })
          .eq("client", client)
          .neq("bucket", "done"),
      ]);

      const m = meetings.data?.[0];
      const synced_at =
        meetings.data?.[0]?.updated_at ??
        recent.data?.[0]?.updated_at ?? null;

      return {
        client,
        last_meeting_at: m?.updated_at ?? null,
        last_meeting_title: m?.title ?? null,
        open_projects: projects.count ?? 0,
        updates_7d: recent.count ?? 0,
        open_tasks: tasks.count ?? 0,
        synced_at,
      };
    },
  });
}
